/**
 * SITE HEADER
 *
 * @returns {Promise<void>}
 */
import observeExitIntent from 'exit-intent-js';
import {getAllMenuCookies} from '@/js/funnel/_mixins/menu-cookie';

const siteHeader = async () => {
    const [
        {default: Vue},
        {VBToggle},
        {NavPlugin},
        {BNavItemDropdown},
        {AjaxMixin},
        {default: Feather},
        {ModalPlugin},
    ] = await Promise.all([
        import(/* webpackChunkName: "vue" */ 'vue'),
        import(/* webpackChunkName: "bs-toggle" */ 'bootstrap-vue'),
        import(/* webpackChunkName: "bs-nav" */ 'bootstrap-vue'),
        import(/* webpackChunkName: "bs-nav-item-dropdown" */ 'bootstrap-vue'),
        import(/* webpackChunkName: "global-mixins-ajax" */ '@mymyMixins/ajax'),
        import(/* webpackChunkName: "feather-icons" */ 'vue-feather/src/feather'),
        import(/* webpackChunkName: "bs-modal" */ 'bootstrap-vue'),
    ]);

    Vue.use(VBToggle)
    Vue.use(NavPlugin)
    Vue.use(ModalPlugin)

    // Create our vue instance
    const vm = new Vue({
        name: 'SiteHeader',
        el: '#site-header',
        delimiters: ['[[', ']]'],
        mixins: [AjaxMixin],
        directives: {
            'b-toggle': VBToggle,
        },
        components: {
            'feather-icon': Feather,
        },
        data: {
            cartsNav: [],
            mainNavActive: false,
            mainNavActiveItem: null,
            isLoading: false,
            modals: {
                exitIntent: {
                    showed: localStorage.getItem('exit-intent-id') ? true : false,
                    show: false,
                    showSendButton: false,
                    success: false,
                    messageFeedback: false,
                    errors: [],
                    fields: {
                        exitIntentId: '',
                        exitIntentReason: '',
                        exitIntentMessage: '',
                    }
                }
            },
        },
        async mounted() {
            // fetch data about the currently logged-in user and show login/logout buttons
            const response = await this.makePostRequest('mymy/settings/site-header-data', {
                menuCookies: this.getMenuCookies(),
            });

            // load up Vue's local data with the response data
            for (const key in response.data) {
                this[key] = response.data[key];
            }

            let heroGetQuoteButton = document.querySelector('.get-quote-button');
            if(heroGetQuoteButton) {
                heroGetQuoteButton.style.display = this.showGetQuote ? 'inline-block' : 'none'
            }

            const { destroy } = observeExitIntent({
                debug: true,
                timeOnPage: 0,
                idleTime: 300000, // 5 mins,
                scrollUpThreshold: 0,
            });

            window.addEventListener('exit-intent', e => {
                console.log('Exit intent detected header! Reason: ' + e.detail);
                destroy();
                if(this.modals.exitIntent.showed) {
                    return;
                }
                this.modals.exitIntent.show = true
            });
        },
        created() {
            window.addEventListener("resize", this.myEventHandler);
        },
        destroyed() {
            window.removeEventListener("resize", this.myEventHandler);
        },
        methods: {
            myEventHandler(e) {
                if(e.target.innerWidth >= 992) {
                    this.mainNavActive = false;
                    this.mainNavActiveItem = null;
                }
            },

            toggleMainNav() {
                this.mainNavActive = !this.mainNavActive;
            },

            navItemClicked(navItemId) {
                if(!this.mainNavActive) {
                    return;
                }
                if(this.mainNavActiveItem == navItemId) {
                    return;
                }

                event.preventDefault();
                this.mainNavActiveItem = navItemId;
            },

            /**
             * Get all the "menu cookies" from the customer's browser that have cart numbers and
             * match the current site's country ISO code.
             *
             * @return {*[]}
             */
            getMenuCookies() {
                let menuCookies = [];
                const allMenuCookies = getAllMenuCookies();
                for (const cookieName in allMenuCookies) {
                    const cookieValue = allMenuCookies[cookieName];
                    const cartNumber = cookieValue.n ?? null;
                    if (typeof cartNumber === 'string' && cartNumber) {
                        const menu = cookieName.split('_');
                        menuCookies.push({
                            country: menu[2],
                            id: parseInt(menu[1]),
                            number: cartNumber,
                        });
                    }
                }

                return menuCookies;
            },

            /**
             * When the customer submits the "Exit Intent" form
             *
             * @return {Promise<void>}
             */
            async onSubmitExitIntentForm() {
                this.isLoading = true;
                    
                const response = await this.makePostRequest(
                    'mymy/forms/set-exit-intent',
                    {
                        fields: this.modals.exitIntent.fields
                    }
                );

                this.isLoading = false;
                if (response?.data?.success) {
                    this.modals.exitIntent.success = true;
                    this.modals.exitIntent.messageFeedback = false;
                    this.modals.exitIntent.fields = response.data.fields;
                    localStorage.setItem('exit-intent-id', this.modals.exitIntent.fields.exitIntentId)
                    this.modals.exitIntent.showed = true;
                    let close = false;
                    if(this.isMessageRequired && this.modals.exitIntent.fields.exitIntentMessage != '') {
                        close = true;
                    }
                    if(close) {
                        // setTimeout(() => {
                        //     this.modals.exitIntent.show = false;
                        // }, 5000)
                        this.modals.exitIntent.messageFeedback = true;
                    }
                }
                else if (response?.data?.errors) {
                    this.modals.exitIntent.success = false;
                    this.modals.exitIntent.errors = this.getYiiErrors(response.data.errors);
                }
                else {
                    if(window.Sentry) {
                        Sentry.captureMessage("Response not handled correctly")
                    }
                }
            }
        },
        computed: {
            showGetQuote() {
                if(!window.posthog) {
                    return true;
                }
                return (window.posthog.getFeatureFlag('request-a-quote-button-removal') === 'control');
            },
            isMessageRequired() {
                return this.modals.exitIntent.fields.exitIntentReason === 'hadTechnicalIssue';
            },

            isJustResearching() {
                return this.modals.exitIntent.fields.exitIntentReason === 'justResearching';
            },
            isCantUnderstandHowItWorks() {
                return this.modals.exitIntent.fields.exitIntentReason === 'cantUnderstandHowItWorks';
            },
            isCantFindPricing() {
                return this.modals.exitIntent.fields.exitIntentReason === 'cantFindPricing';
            },
            isNotReadyYet() {
                return this.modals.exitIntent.fields.exitIntentReason === 'notReadyYet';
            },
            isNeedApproval() {
                return this.modals.exitIntent.fields.exitIntentReason === 'needApproval';
            },
            isComparingOtherProviders() {
                return this.modals.exitIntent.fields.exitIntentReason === 'comparingOtherProviders';
            },
            isWouldLikePersonalAdvice() {
                return this.modals.exitIntent.fields.exitIntentReason === 'wouldLikePersonalAdvice';
            },
            isHadTechnicalIssue() {
                return this.modals.exitIntent.fields.exitIntentReason === 'hadTechnicalIssue';
            }
        },
        watch: {
            'modals.exitIntent.fields.exitIntentReason': {
                async handler(newValue, oldValue) {
                    await this.onSubmitExitIntentForm()
                }
            },
        }
    });

    return vm;
};

// Execute async function
siteHeader().then( (vm) => {
});